import { useMemo } from 'react';
import {
  Box,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TestIds } from '../../../../assets/testIds';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { usePartnerSpecificModalConfigMap } from './PartnerSpecificFormConfig';
import { getFormattedTestId } from '../../Content/ContentCommonFunctions';
import OptimusButton from '../../../../common/library/Button/Button';

interface ManageEnrollmentCardProps {
  partnerName?: string;
  partnerCode?: string;
  setShowUnlink: (event: boolean) => void;
}

export function ManageEnrollmentCard({
  partnerName,
  partnerCode,
  setShowUnlink,
}: ManageEnrollmentCardProps) {
  const theme = useTheme();
  const { openExternalLink } = useNavigationUtils();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const partnerSpecificModalConfigMap = usePartnerSpecificModalConfigMap();
  const partnerConfig = useMemo(() => {
    if (!partnerCode || !(partnerCode in partnerSpecificModalConfigMap)) {
      return undefined; // or some default config
    }
    return partnerSpecificModalConfigMap[partnerCode];
  }, [partnerCode, partnerSpecificModalConfigMap]);

  const handleClick = () => {
    if (partnerConfig?.isManageRewardExternalLink) {
      navigate(partnerConfig?.manageRewardsBtnLink);
    } else {
      openExternalLink(partnerConfig?.manageRewardsBtnLink || '');
    }
  };

  return (
    <Box>
      <Divider sx={{ marginBottom: theme.spacing(3) }} />
      <Box sx={{
        marginBottom: theme.spacing(3),
        color: theme.palette.grey[500],
      }}>
        {partnerConfig?.manageRewardsDescriptionKey &&
          partnerConfig?.manageRewardsDescriptionLink ?
          (<Typography component="span">
            <Trans
              i18nKey={partnerConfig.manageRewardsDescriptionKey}
              t={t}
              components={{
                linkTag: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <Typography
                    component={'span'}
                    onClick={() => openExternalLink(partnerConfig.manageRewardsDescriptionLink || '')}
                    sx={{ color: theme.palette.brand.primary, cursor: 'pointer' }}
                  />
                ),
              }}
            />
          </Typography>) :
          (<Typography component='span'>
            {partnerConfig?.manageRewardsDescription}
          </Typography>)
        }
      </Box>
      <Divider sx={{ marginBottom: theme.spacing(3) }} />
      <Box>
        <OptimusButton
          buttonText={partnerConfig?.manageRewardsBtnText || ''}
          data-testid={`${TestIds.rewardsPage.signUpForm.partnerLinkBtn}-${getFormattedTestId(partnerName || '')}`}
          onClick={handleClick}
          sx={{
            marginBottom: theme.spacing(2),
          }}
        />
        <OptimusButton
          customVariant='secondaryGray'
          buttonText={`Unlink this ${partnerName} account`}
          data-testid={`${TestIds.rewardsPage.signUpForm.unlinkBtn}-${getFormattedTestId(partnerName || '')}`}
          onClick={() => setShowUnlink(true)}
        />
      </Box>
    </Box>
  );
}
